type WacManagementRegenerationRequestOptions = {
	route: string;
	locale: string;
	messageDeduplicationId: string;
	environment: string;
	forceRegeneration: boolean;
	signal: AbortSignal;
};

type WacManagementRegenerationRequest = {
	endpoint: string;
	requestOptions: {
		method: 'POST';
		headers: Record<string, string>;
		signal: AbortSignal;
		body: string;
	};
};

const isWacLocalDevelopment = (): boolean =>
	process.env['VERSION'] === 'local' ||
	process.env['MODE'] === 'dev' ||
	process.env['CLIENT_LIBRARY_ENV'] === 'local';

const getWacEnv = (): string =>
	isWacLocalDevelopment() ? 'local' : process.env['CLIENT_LIBRARY_ENV'] || 'staging';

const getWacManagementRegenerationServiceEnv = (): string => {
	switch (getWacEnv()) {
		case 'production':
			return 'prod';
		case 'development':
			return 'dev';
		case 'local':
		case 'staging':
		default:
			return 'staging';
	}
};

const getWacManagementRegenerationServiceRegion = (): string => {
	switch (getWacEnv()) {
		case 'development':
			return 'ap-southeast-2';
		case 'local':
		case 'production':
		case 'staging':
		default:
			return 'us-east-1';
	}
};

export const getWacManagementRegenerationEndpoint = (): string => {
	const endpoint = process.env['WAC_MANAGEMENT_REGENERATION_ENDPOINT'] || '/api/v1/regenerate';

	try {
		const url = new URL(endpoint);
		return `${url.pathname}${url.search}`;
	} catch {
		const [firstPathSegment] = endpoint.split('/');
		const looksLikeHostname =
			(firstPathSegment as string).includes('.') || (firstPathSegment as string).includes(':');

		if (looksLikeHostname) {
			const url = new URL(`https://${endpoint}`);
			return `${url.pathname}${url.search}`;
		}

		return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
	}
};

export const getWacManagementRegenerationRequest = ({
	route,
	locale,
	messageDeduplicationId,
	environment,
	forceRegeneration,
	signal,
}: WacManagementRegenerationRequestOptions): WacManagementRegenerationRequest => {
	const serviceEnv = getWacManagementRegenerationServiceEnv();
	const serviceRegion = getWacManagementRegenerationServiceRegion();

	return {
		endpoint: getWacManagementRegenerationEndpoint(),
		requestOptions: {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'x-ssr-service-name': 'wac-management-service',
				'x-ssr-service-region': serviceRegion,
				'x-ssr-service-env': serviceEnv,
			},
			signal,
			body: JSON.stringify(
				{
					route,
					locale,
					MessageDeduplicationId: messageDeduplicationId,
					environment,
					forceRegeneration,
				},
				null,
				'\t',
			),
		},
	};
};

export const getWacManagementLiveCompositionEndpoint = (): string => {
	const endpoint =
		process.env['WAC_MANAGEMENT_LIVE_COMPOSITION_ENDPOINT'] || '/api/v1/uniform/composition/get';
	try {
		const url = new URL(endpoint);
		return `${url.pathname}${url.search}`;
	} catch {
		const [firstPathSegment] = endpoint.split('/');
		const looksLikeHostname =
			(firstPathSegment as string).includes('.') || (firstPathSegment as string).includes(':');
		if (looksLikeHostname) {
			const url = new URL(`https://${endpoint}`);
			return `${url.pathname}${url.search}`;
		}
		return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
	}
};

export const getWacManagementLiveCompositionRequest = (
	queryParams: URLSearchParams,
	signal: AbortSignal,
): { endpoint: string; requestOptions: RequestInit } => {
	const serviceEnv = getWacManagementRegenerationServiceEnv();
	const serviceRegion = getWacManagementRegenerationServiceRegion();
	const endpointPath = getWacManagementLiveCompositionEndpoint();
	const endpoint = `${endpointPath}?${queryParams.toString()}`;
	return {
		endpoint,
		requestOptions: {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				'x-ssr-service-name': 'wac-management-service',
				'x-ssr-service-region': serviceRegion,
				'x-ssr-service-env': serviceEnv,
			},
			signal,
		},
	};
};
